import * as S from "./styled";
import { NumericFormat } from "react-number-format";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import axiosInstance from "services/axiosInstance";
/**
 * Components
 */
import LayoutModal from "../../../../components/LayoutModal";
import { useEffect, useState } from "react";
/**
 * hooks
 */
import apiRequest from "../../../../hooks/apiRequest";
import useLoading from '../../../../hooks/useLoading';
/**
 * Icons
 */
import { Calculator, Eyeglasses } from "@phosphor-icons/react";
/**
 * Interface
*/
import { Inputs, IFrame } from "Interfaces/IFrame";
import { ISupplier } from "Interfaces/ISupllier";

interface ICreate {
  isOpenModal: boolean;
  setModalClose: () => void;
  refreshFrames: () => void; // Adicionada a função de atualização
}

export default function Create({
  isOpenModal,
  setModalClose,
  refreshFrames,
}: ICreate) {
  const {
    register,
    reset,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Inputs>();

  const [material, setMaterial] = useState<IFrame['materials'][]>([]);
  const [supplier, setSupplier] = useState<ISupplier[]>([]);
  const [grife, setGrife] = useState<IFrame["brands"][]>([]);
  const { isLoading, startLoading, stopLoading } = useLoading();

  const fetchData = async () => {
    try {
      const [materialResponse, supplierResponse, brandResponse] =
        await Promise.all([
          axiosInstance.get(`material`),
          axiosInstance.get(`supplier-get-all`),
          axiosInstance.get(`brand`),
        ]);
      setGrife(brandResponse.data);
      setMaterial(materialResponse.data);
      setSupplier(supplierResponse.data);
    } catch (error) {
      console.log("Error: ", error);
    }
  };

  useEffect(() => {
    if (isOpenModal) {
      fetchData();
    }
  }, [isOpenModal]);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    startLoading();
    try {
      const response = await apiRequest(`frame`, 'POST', data);
      if (response.status === 201) {
        alert("Armação criada com sucesso!");
        refreshFrames(); // Atualiza os dados na tabela
        onClose(); // Fechar o modal após sucesso
      }
    } catch (error: any) {
      alert(error.message); // Exibe a mensagem de erro lançada pelo serviço
    } finally {
      stopLoading();
    }
  };

  const onClose = () => {
    setModalClose();
    reset();
  };

  const CalculateValues = () => {
    const qtd = watch("amount") || 0; // Certifique-se de que o valor é um número
    const valueCompra = watch("purchase_value") || 0;
    const valueVenda = Number(watch("price") || 0); // Valor da venda
    const lucro = watch("profit") || 0;

    // Verificar se os campos obrigatórios estão preenchidos
    if (!qtd || !valueCompra) {
      alert(
        "Para realizar o cálculo, é necessário informar a quantidade e o valor da compra!"
      );
      return; // Interrompe a execução caso os valores sejam inválidos
    }

    // Calcular o resultado base
    const result = valueCompra / qtd;

    // Caso 1: Calcular o lucro
    if (qtd && valueCompra && valueVenda && !lucro) {
      const calculatedLucro = ((valueVenda - result) / result) * 100;
      setValue("profit", calculatedLucro); // Atualiza o campo de lucro
      return;
    }

    // Calcular o resultado com lucro
    const resultLucro = result + (result * lucro) / 100;

    setValue("price", resultLucro.toString());
  };

  if (isOpenModal) {
    return (
      <LayoutModal
        statusLoad={isLoading}
        title="Registrar Armação"
        subTitle="Cadastre uma nova armação em sua base de dados."
        setModalClose={onClose}
        submiteModal={handleSubmit(onSubmit)}

        nameIconMinimize={'Registrar Armação'}
        setMaximizeModal={fetchData}
      >
        <S.Container>
          <S.SeparateDiv>
            <S.FormInput>
              <S.Label>
                Código da Armação
                <span>*</span>
              </S.Label>
              <S.Input
                placeholder="Informe o código da armação"
                type="text"
                {...register("code", { required: true })}
              />
              {errors.code && <S.InforErro>Campo Obrigatório</S.InforErro>}
            </S.FormInput>

            <S.FormInput>
              <S.Label>Cor</S.Label>
              <S.Input placeholder="Informe a cor" {...register("color")} />
            </S.FormInput>
          </S.SeparateDiv>

          <S.SeparateDiv>
            <S.FormInput>
              <S.Label>
                Tamanho
                {/* <span>*</span> */}
              </S.Label>
              <S.Input
                placeholder="Informe o tamanho"
                type="number"
                {...register("size")}
              />
              {errors.size && <S.InforErro>Campo Obrigatório</S.InforErro>}
            </S.FormInput>

            <S.FormInput>
              <S.Label>
                Haste
                {/* <span>*</span> */}
              </S.Label>
              <S.Input
                placeholder="Informe a haste"
                type="number"
                {...register("haste")}
              />
              {errors.haste && <S.InforErro>Campo Obrigatório</S.InforErro>}
            </S.FormInput>

            <S.FormInput>
              <S.Label>
                Ponte
                {/* <span>*</span> */}
              </S.Label>
              <S.Input
                placeholder="Informe a ponta"
                type="number"
                {...register("bridge")}
              />
              {errors.bridge && <S.InforErro>Campo Obrigatório</S.InforErro>}
            </S.FormInput>
          </S.SeparateDiv>

          <S.SeparateDiv>
            <S.FormInput>
              <S.Label>
                Tipo de armação
                {/* <span>*</span> */}
              </S.Label>
              <S.Select {...register("material_id")}>
                <option value="" disabled selected hidden>
                  Selecione
                </option>
                {material.map((val) => {
                  return <option value={val.id}>{val.material}</option>;
                })}
              </S.Select>
              {errors.material_id && (
                <S.InforErro>Campo Obrigatório</S.InforErro>
              )}
            </S.FormInput>

            <S.FormInput>
              <S.Label>
                Fornecedor
                {/* <span>*</span> */}
              </S.Label>
              <S.Select {...register("supplier_id")}>
                <option value="" disabled selected hidden>
                  Selecione
                </option>
                {supplier.map((val) => {
                  return <option value={val.id}>{val.name}</option>;
                })}
              </S.Select>
              {errors.supplier_id && (
                <S.InforErro>Campo Obrigatório</S.InforErro>
              )}
            </S.FormInput>

            <S.FormInput>
              <S.Label>
                Grife
                {/* <span>*</span> */}
              </S.Label>
              <S.Select {...register("brand_id")}>
                <option value="" disabled selected hidden>
                  Selecione
                </option>
                {grife.map((val) => {
                  return <option value={val.id}>{val.brand}</option>;
                })}
              </S.Select>
              {errors.brand_id && <S.InforErro>Campo Obrigatório</S.InforErro>}
            </S.FormInput>
          </S.SeparateDiv>

          {/* Campo de Valores */}
          <S.ModalCalculate>
            <S.HeadCalc>
              <p>Calcular valor da venda</p>
              <S.ButtonCalc onClick={CalculateValues}>
                <Calculator size={18} />
              </S.ButtonCalc>
            </S.HeadCalc>

            <S.SeparateDiv>
              <S.FormInput>
                <S.Label>
                  QTD
                  <span>*</span>
                </S.Label>
                <S.Input
                  placeholder="0"
                  type="number"
                  {...register("amount", { required: true })}
                />
                {errors.amount && <S.InforErro>Campo Obrigatório</S.InforErro>}
              </S.FormInput>

              <S.FormInput>
                <S.Label>Valor da Compra R$</S.Label>
                <Controller
                  name="purchase_value"
                  control={control}
                  render={({ field: { onChange, value, ref } }) => (
                    <NumericFormat
                      thousandSeparator="."
                      decimalSeparator=","
                      decimalScale={2}
                      fixedDecimalScale={true}
                      placeholder="R$ 0,00"
                      prefix="R$ "
                      value={value}
                      onValueChange={(values) => {
                        const purchaseValue = values.floatValue ?? 0;
                        onChange(purchaseValue); // Atualiza o valor de compra
                      }}
                      getInputRef={ref}
                    />
                  )}
                />
              </S.FormInput>
            </S.SeparateDiv>

            <S.SeparateDiv>
              <S.FormInput>
                <S.Label>Lucro %</S.Label>
                <Controller
                  name="profit"
                  control={control}
                  render={({ field: { onChange, value, ref } }) => (
                    <NumericFormat
                      thousandSeparator="."
                      decimalSeparator=","
                      decimalScale={2}
                      fixedDecimalScale={true}
                      placeholder="0,00 %"
                      suffix=" %"
                      value={value}
                      onValueChange={(values) => {
                        const purchaseValue = values.floatValue ?? 0;
                        onChange(purchaseValue); // Atualiza o valor de compra
                      }}
                      getInputRef={ref}
                    />
                  )}
                />
              </S.FormInput>

              <S.FormInput>
                <S.Label>
                  Valor da Venda R$
                  <span>*</span>
                </S.Label>
                <Controller
                  name="price"
                  control={control}
                  rules={{ required: "Este campo é obrigatório" }}
                  render={({ field: { onChange, value, ref } }) => (
                    <NumericFormat
                      thousandSeparator="."
                      decimalSeparator=","
                      decimalScale={2}
                      fixedDecimalScale={true}
                      placeholder="R$ 0,00"
                      prefix="R$ "
                      value={value}
                      onValueChange={(values) => {
                        const purchaseValue = values.floatValue ?? 0;
                        onChange(purchaseValue);
                      }}
                      getInputRef={ref}
                    />
                  )}
                />
                {errors.price && <S.InforErro>Campo Obrigatório</S.InforErro>}
              </S.FormInput>
            </S.SeparateDiv>
          </S.ModalCalculate>
          {/* Fim Campo de Valores */}

          <S.FormInput>
            <S.Label>Descrição</S.Label>
            <S.TextArea
              placeholder="Campo Opcional"
              rows={2}
              {...register("description")}
            />
          </S.FormInput>
        </S.Container>
      </LayoutModal>
    );
  }
  return null;
}
